import React, {ChangeEvent, useEffect, useState} from 'react'
import {RadioGroup} from 'src/components';
import {familyMembersSelector} from "src/EstateFlowchart/slices/EstateFlowChartSlice";
import {useAppSelector} from 'src/store/hooks';
import {RevocableTrustModalInputs, Trustee} from "src/EstateFlowchart/model/EstateFlowchartTypes";
import {getPrimaryAndPartnerMemberNames} from "../../utils/DefUtils";

type RevocableTrustInputsProps = Readonly<{
    setFormData: Function,
    formData: RevocableTrustModalInputs
}>

function RevocableTrustInputs({setFormData, formData}: RevocableTrustInputsProps) {
    const familyMembers = useAppSelector(familyMembersSelector);
    let {primaryMemberName, partnerMemberName} = getPrimaryAndPartnerMemberNames(familyMembers);
    if (partnerMemberName === primaryMemberName) {
        partnerMemberName = partnerMemberName + " "; // append whitespace to differentiate primary from partner in radio buttons when names are identical
    }

    const [isFirstDeathDisabled, setIsFirstDeathDisabled] = useState<boolean>(true);
    const revTrustTypeValues = familyMembers.partnerMember ?
        [`Individual Trust for ${primaryMemberName}`, `Individual Trust for ${partnerMemberName}`, "Joint Trust"] :
        [`Individual Trust for ${primaryMemberName}`];
    const assumedFirstDeathValues = familyMembers.partnerMember ?
        [primaryMemberName, partnerMemberName] :
        [primaryMemberName];
    const distributionStructureValues = familyMembers.partnerMember ?
        ["Leave to spouse, then remainder beneficiaries", "Split among remainder beneficiaries"] :
        ["Split among remainder beneficiaries"];

    useEffect(() => {
        //setting initial data in here based on available options
        setFormData((prev: RevocableTrustModalInputs) => ({
            ...prev,
            revTrustType: [
                {
                    memberId: familyMembers.primaryMember.id
                } as Trustee
            ],
            assumedFirstDeath: familyMembers.primaryMember.id,
            distributionStructure: distributionStructureValues[0]
        }));
    }, []);

    const handleAssumedFirstDeathChange = (e: ChangeEvent<HTMLInputElement>) => {
        const assumedFirstDeathId = e.target.value === primaryMemberName ? familyMembers.primaryMember.id : familyMembers.partnerMember?.id;
        setFormData((prev: RevocableTrustModalInputs) => ({
            ...prev,
            assumedFirstDeath: assumedFirstDeathId
        }));
    }

    return (
        <div>
            <RadioGroup
                name={`revTrust-type-radio`}
                label="What type of revocable trust would you like to create?"
                className='vertical-radiogroup layout-data-entry-form__field flowchart-modal__field'
                layout='vertical'
                values={revTrustTypeValues}
                disabled={!familyMembers.partnerMember}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    let firstDeath: string | undefined;
                    let members = [] as Trustee[];
                    let distributionStructure = distributionStructureValues[0];

                    if (e.target.value === `Individual Trust for ${primaryMemberName}`) {
                        firstDeath = familyMembers.primaryMember.id;
                        members.push({memberId: familyMembers.primaryMember.id} as Trustee);
                        setIsFirstDeathDisabled(true)
                    } else if (e.target.value === `Individual Trust for ${partnerMemberName}`) {
                        firstDeath = familyMembers.partnerMember?.id;
                        members.push({memberId: familyMembers?.partnerMember?.id ?? ''} as Trustee);
                        setIsFirstDeathDisabled(true)
                    } else {
                        firstDeath = familyMembers.primaryMember.id;
                        members.push({memberId: familyMembers.primaryMember.id} as Trustee);
                        members.push({memberId: familyMembers?.partnerMember?.id ?? ''} as Trustee);
                        setIsFirstDeathDisabled(false)
                    }
                    setFormData((prev: RevocableTrustModalInputs) => ({
                        ...prev,
                        revTrustType: members,
                        assumedFirstDeath: firstDeath,
                        distributionStructure
                    }));
                }}
                selected={revTrustTypeValues[0]}
            />
            <RadioGroup
                name={'revTrust-firstDeath-radio'}
                label="Assumed First Death"
                className='vertical-radiogroup layout-data-entry-form__field flowchart-modal__field'
                layout='vertical'
                values={assumedFirstDeathValues}
                disabled={isFirstDeathDisabled || !familyMembers.partnerMember}
                onChange={handleAssumedFirstDeathChange}
                selected={formData.assumedFirstDeath === familyMembers?.partnerMember?.id ? partnerMemberName : primaryMemberName}
            />
            <RadioGroup
                name={'revTrust-distirbutionStructure-radio'}
                label="Select distribution structure at Grantor's death"
                className='vertical-radiogroup layout-data-entry-form__field flowchart-modal__field'
                layout='vertical'
                values={distributionStructureValues}
                disabled={!familyMembers.partnerMember || (formData.revTrustType.length > 1)}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev: RevocableTrustModalInputs) => ({
                        ...prev,
                        distributionStructure: e.target.value
                    }));
                }}
                selected={formData.distributionStructure}
            />
        </div>
    )
}

export default RevocableTrustInputs